import { useState } from 'react';
import { motion } from 'framer-motion';
import { Sun, Moon } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

// ─── ThemeSwitcher: Dark to Light Switch ──────────────────────────────────────
// A tactile, animated toggle switch for switching between Dark and Light mode.

export default function ThemeSwitcher() {
  const { isDark, toggleTheme, isAutoTheme } = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div className="relative flex items-center shrink-0">
      <button
        type="button"
        role="switch"
        aria-checked={!isDark}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        onClick={toggleTheme}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`group relative flex items-center w-[52px] h-[28px] rounded-full p-[3px] transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary cursor-pointer select-none ${
          isDark
            ? 'bg-[#120f26]/90 border border-purple-500/30 hover:border-purple-400/50 shadow-[0_0_12px_rgba(168,85,247,0.15)]'
            : 'bg-gradient-to-r from-amber-100/90 to-indigo-100/90 border border-amber-400/40 hover:border-amber-400/70 shadow-[0_0_12px_rgba(245,158,11,0.2)]'
        }`}
      >
        {/* Background Track Icons */}
        <div className="absolute inset-0 px-2 flex items-center justify-between pointer-events-none">
          {/* Sun icon on left of track */}
          <Sun
            size={12}
            className={`transition-all duration-300 ${
              isDark
                ? 'text-amber-400/30 group-hover:text-amber-400/50 scale-90'
                : 'text-amber-500 opacity-0 scale-75'
            }`}
          />
          {/* Moon icon on right of track */}
          <Moon
            size={12}
            className={`transition-all duration-300 ${
              isDark
                ? 'text-purple-300 opacity-0 scale-75'
                : 'text-indigo-500/40 group-hover:text-indigo-500/60 scale-90'
            }`}
          />
        </div>

        {/* Sliding Thumb / Knob */}
        <motion.div
          layout
          transition={{
            type: 'spring',
            stiffness: 600,
            damping: 32,
          }}
          className={`relative z-10 w-[22px] h-[22px] rounded-full flex items-center justify-center transition-shadow duration-300 ${
            isDark
              ? 'ml-auto bg-gradient-to-tr from-purple-600 to-indigo-500 text-white shadow-[0_2px_8px_rgba(147,51,234,0.6)]'
              : 'mr-auto bg-gradient-to-tr from-amber-400 to-yellow-300 text-amber-950 shadow-[0_2px_8px_rgba(245,158,11,0.6)]'
          }`}
        >
          {isDark ? (
            <motion.div
              key="moon"
              initial={{ rotate: -45, scale: 0.7, opacity: 0 }}
              animate={{ rotate: 0, scale: 1, opacity: 1 }}
              exit={{ rotate: 45, scale: 0.7, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Moon size={13} className="fill-purple-100 text-purple-100" />
            </motion.div>
          ) : (
            <motion.div
              key="sun"
              initial={{ rotate: 45, scale: 0.7, opacity: 0 }}
              animate={{ rotate: 0, scale: 1, opacity: 1 }}
              exit={{ rotate: -45, scale: 0.7, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Sun size={13} className="fill-amber-950 text-amber-950" />
            </motion.div>
          )}
        </motion.div>

        {/* Auto-IST indicator dot */}
        {isAutoTheme && (
          <span
            className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ring-1 ${
              isDark
                ? 'bg-purple-400 ring-[#120f26]'
                : 'bg-amber-400 ring-white'
            }`}
            title="Auto: following IST day/night"
          />
        )}
      </button>

      {/* Floating Hover Tooltip */}
      {isHovered && (
        <motion.div
          initial={{ opacity: 0, y: 4, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 4, scale: 0.95 }}
          transition={{ duration: 0.15 }}
          className="pointer-events-none absolute top-[calc(100%+8px)] left-1/2 -translate-x-1/2 z-[100] whitespace-nowrap"
        >
          <div
            className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold tracking-wide shadow-xl backdrop-blur-md border ${
              isDark
                ? 'bg-slate-950/90 text-purple-200 border-white/10'
                : 'bg-white/95 text-slate-800 border-black/10'
            }`}
          >
            {isAutoTheme
              ? `Auto IST · ${isDark ? 'Switch to light' : 'Switch to dark'}`
              : (isDark ? 'Switch to light mode' : 'Switch to dark mode')}
          </div>
        </motion.div>
      )}
    </div>
  );
}
